import React from "react";
import Textarea from "../../../componentLibrary/Textarea";
import type {
  InputQuestionI,
  MultipleChoiceQuestionI,
  RatingScaleQuestionI,
  ToolboxAnswerValue,
  ToolboxAnswers,
  ToolboxQuestionI,
} from "../../../types";
interface InputQuestionProps {
  question: InputQuestionI;
  value: ToolboxAnswerValue | undefined;
  onChange: (variableName: string, value: ToolboxAnswerValue) => void;
}

interface MultipleChoiceQuestionProps {
  question: MultipleChoiceQuestionI;
  value: ToolboxAnswerValue | undefined;
  onChange: (variableName: string, value: ToolboxAnswerValue) => void;
}

interface RatingScaleQuestionProps {
  question: RatingScaleQuestionI;
  value: ToolboxAnswerValue | undefined;
  onChange: (variableName: string, value: ToolboxAnswerValue) => void;
}

function InputQuestion({ question, value, onChange }: InputQuestionProps) {
  return (
    <div className="space-y-1">
      {question.content.label && (
        <label className="block text-sm font-medium text-gray-700">
          {question.content.label}
        </label>
      )}
      <Textarea
        placeholder={question.content.placeholder ?? ""}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(question.variableName, e.target.value)}
      />
    </div>
  );
}

function MultipleChoiceQuestion({
  question,
  value,
  onChange,
}: MultipleChoiceQuestionProps) {
  const choices: string[] = question.content.choices ?? [];
  const msq: boolean = question.content.msq ?? false;

  const selected = msq
    ? Array.isArray(value)
      ? value
      : value
        ? [value as string]
        : []
    : Array.isArray(value)
      ? (value[0] ?? "")
      : ((value as string) ?? "");

  const handleChange = (choice: string) => {
    if (msq) {
      const current = Array.isArray(value)
        ? value
        : value
          ? [value as string]
          : [];
      const next = current.includes(choice)
        ? current.filter((c) => c !== choice)
        : [...current, choice];
      onChange(question.variableName, next);
    } else {
      onChange(question.variableName, choice);
    }
  };

  return (
    <div className="space-y-2">
      {question.content.label && (
        <label className="block text-sm font-medium text-gray-700">
          {question.content.label}
        </label>
      )}
      <div className="space-y-1.5">
        {choices.map((choice) => {
          const isSelected = msq
            ? selected.includes(choice)
            : selected === choice;
          return (
            <button
              key={choice}
              type="button"
              onClick={() => handleChange(choice)}
              className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                isSelected
                  ? "border-electrum-300 bg-electrum-50 text-electrum font-medium"
                  : "border-gray-200 bg-white text-gray-700 hover:border-electrum-300 hover:bg-electrum-50"
              }`}
            >
              {choice}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RatingScaleQuestion({
  question,
  value,
  onChange,
}: RatingScaleQuestionProps) {
  const scale: number = question.content.scale ?? 5;
  const steps = Array.from({ length: scale }, (_, i) => i + 1);
  const current = value as number | null;

  return (
    <div className="space-y-2">
      {question.content.label && (
        <label className="block text-sm font-medium text-gray-700">
          {question.content.label}
        </label>
      )}
      <div className="flex gap-2 flex-wrap">
        {steps.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(question.variableName, n)}
            className={`w-10 h-10 rounded-full border text-sm font-semibold transition-colors ${
              current === n
                ? "border-electrum-300 bg-electrum-300 text-white"
                : "border-gray-300 bg-white text-gray-600 hover:border-electrum-300 hover:text-electrum-300"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {(question.content.minLabel || question.content.maxLabel) && (
        <div className="flex justify-between text-xs text-gray-400 px-1">
          <span>{question.content.minLabel ?? ""}</span>
          <span>{question.content.maxLabel ?? ""}</span>
        </div>
      )}
    </div>
  );
}

export function renderToolboxQuestion(
  question: ToolboxQuestionI,
  answers: ToolboxAnswers,
  onChange: (variableName: string, value: ToolboxAnswerValue) => void,
): React.ReactNode {
  const value = answers[question.variableName];

  switch (question.type) {
    case "input":
      return (
        <InputQuestion
          key={question.questionId}
          question={question}
          value={value}
          onChange={onChange}
        />
      );
    case "multiple_choice":
      return (
        <MultipleChoiceQuestion
          key={question.questionId}
          question={question}
          value={value}
          onChange={onChange}
        />
      );
    case "rating_scale":
      return (
        <RatingScaleQuestion
          key={question.questionId}
          question={question}
          value={value}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
}
